import React, { useEffect, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Header } from "../../components/layout";
import Pagination from "../../components/common/Pagination/Pagination";
import StatsCard from "../../components/modules/overview/StatsCard/StatsCard";
import AttackVectorCard from "../../components/modules/policy/AttackVectorCard";
import UseCaseIdentitiesModal from "../../components/modules/policy/UseCaseIdentitiesModal/UseCaseIdentitiesModal";
import { Shield, TrendingUp, Users } from "lucide-react";
import { FaRobot } from "react-icons/fa";

import {
  fetchVectorsByType,
  fetchPolicyStats,
  setActiveFilterTab,
  setSelectedVectorId,
  setTemplatePage,
} from "../../store/slices/policiesSlice";

import {
  selectSidebarVectors,
  selectPaginatedData,
  selectPaginationInfo,
} from "../../store/selectors/policiesSelectors";

import "./policies.css";
import { Loading } from "../../components/common";
const Policies = () => {
  const dispatch = useDispatch();
  const { loading, error, selectedVectorId, summary } = useSelector(
    (state) => state.policies,
  );
  const activeFilterTab = useSelector(
    (state) => state.policies.activeFilterTab,
  );
  const sidebarVectors = useSelector(selectSidebarVectors);
  const paginatedData = useSelector(selectPaginatedData);
  const paginationInfo = useSelector(selectPaginationInfo);

  const [selectedUseCase, setSelectedUseCase] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch vectors whenever the tab (Human/Non-Human) changes
  useEffect(() => {
    dispatch(fetchVectorsByType(activeFilterTab));
  }, [dispatch, activeFilterTab]);

  const handleUseCaseClick = (useCase, totalIdentities, vectorId) => {
    // Attach current tab type so the modal knows which API path to use
    setSelectedUseCase({
      ...useCase,
      identity_type: activeFilterTab,
      total_identities: totalIdentities,
      vector_id: vectorId,
    });
    setIsModalOpen(true);
  };
  //   // Handle tab change
  const handleTabChange = useCallback(
    (tab) => {
      dispatch(setActiveFilterTab(tab));
    },
    [dispatch],
  );
  //   // Handle vector selection
  const handleVectorSelect = useCallback(
    (vectorId) => {
      // console.log("vectorID",vectorId)
      dispatch(setSelectedVectorId(vectorId));
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(fetchPolicyStats());
  }, [dispatch]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    dispatch(fetchVectorsByType(activeFilterTab));
  };

  const formatVectorValue = (vectorString) => {
    if (!vectorString) return "0";

    // If the backend sends "8/9", this splits it into ['8', '9']
    const parts = vectorString.split("/");

    if (parts.length === 2) {
      const [count, total] = parts;
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px", // Adds a small space between the number and the brackets
          }}
        >
          <strong>{count}</strong>{" "}
          <span
            style={{ opacity: 0.5, fontSize: "0.6em", alignItems: "center" }}
          >
            (out of {total})
          </span>
        </span>
      );
    }

    return vectorString; // Fallback just in case the format changes
  };

  return (
    <div className="identities-page">
      <Header
        title="Attack Vectors"
        subtitle="Identity risk exposure breakdown"
        showSearch={false}
        showSync={false}
        showExport={false}
      />

      <div className="policies-container">
        <div className="policies-summary-grid">
          <StatsCard
            title="Total Attack Vectors"
            value={formatVectorValue(summary?.total_attack_vectors)}
            icon={<Shield size={18} />}
            iconColor="warning"
            loading={loading}
          />
          <StatsCard
            title="Human Vectors"
            value={formatVectorValue(summary?.human_attack_vectors)}
            icon={<Users size={18} />}
            iconColor="success"
            loading={loading}
          />
          <StatsCard
            title="Non-Human Vectors"
            value={formatVectorValue(summary?.non_human_attack_vectors)}
            icon={<FaRobot size={18} />}
            loading={loading}
          />
          <StatsCard
            title="Total Rules Flagged"
            value={summary?.flagged_usecases} // Kept as normal since it's just a number (55)
            icon={<TrendingUp size={18} />}
            iconColor="danger"
            loading={loading}
          />
        </div>

        {error && <div className="policy-error-banner">{error}</div>}

        <div className="policies-main-grid">
          {/* LEFT SECTION: Content Display */}
          <div className="policies-list-section">
            {loading ? (
              <Loading text="loading vectors" />
            ) : (
              <>
                {/* {paginatedData.map(vector => (
                  <AttackVectorCard 
                    key={vector.vector_id} 
                    vector={vector} 
                    isDefaultOpen={selectedVectorId === vector.vector_id}
                    onUseCaseClick={(useCase) => handleUseCaseClick(useCase, useCase.total_identities,vector.vector_id)}
                  />
                ))}
                <Pagination {...paginationInfo} onPageChange={(p) => dispatch(setTemplatePage(p))} /> */}

                <div className="policies-cards-wrapper">
                  {paginatedData && paginatedData.length > 0 ? (
                    paginatedData.map((vector) => (
                      <AttackVectorCard
                        key={vector?.vector_id}
                        vector={vector}
                        isDefaultOpen={selectedVectorId === vector?.vector_id}
                        onUseCaseClick={(useCase) =>
                          handleUseCaseClick(
                            useCase,
                            useCase?.total_identities,
                            vector?.vector_id,
                          )
                        }
                      />
                    ))
                  ) : (
                    <div className="no-data-message">
                      <p>No attack vectors found.</p>
                    </div>
                  )}
                </div>

                {/* 2. Seamlessly pinned at the bottom */}
                <Pagination
                  {...paginationInfo}
                  onPageChange={(p) => dispatch(setTemplatePage(p))}
                />
              </>
            )}
          </div>

          {/* RIGHT SECTION: Sidebar Filters */}
          <div className="category-filter-section">
            <h3 className="section-title">Filter by Vector</h3>

            <div className="policy-tab-toggle" style={{ marginBottom: "15px" }}>
              <button
                className={activeFilterTab === "human" ? "active" : ""}
                onClick={() => handleTabChange("human")}
              >
                Human
              </button>
              <button
                className={activeFilterTab === "non-human" ? "active" : ""}
                onClick={() => handleTabChange("non-human")}
              >
                Non-Human
              </button>
            </div>

            <div className="category-filter-list">
              {sidebarVectors?.map((v) => (
                <div
                  key={v?.vector_id}
                  className={`category-filter-item ${
                    selectedVectorId === v?.vector_id ? "active" : ""
                  }`}
                  onClick={() => handleVectorSelect(v?.vector_id)}
                >
                  <span style={{ fontSize: "13px" }}>
                    {v?.attack_vector_name?.replace(/_/g, " ") ||
                      "Unnamed Vector"}
                  </span>
                  <span>{v?.flagged_rules || 0} R</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Use Case Identities Modal */}
      <UseCaseIdentitiesModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        useCaseData={selectedUseCase}
      />
    </div>
  );
};

export default Policies;
